import { DarkMode, IsDarkModeResult } from '@aparajita/capacitor-dark-mode'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { useEffect, useMemo, useState } from 'react'

import { QUERY_GET_MY_MONITORING_THEME, useGetMyMonitoringTheme } from '@apps-common/apis/my-monitoring/mm-theme'
import { QUERY_GET_PORTAL_THEME, useGetPortalTheme } from '@apps-common/apis/portal/portal-theme/get-portal-theme'
import { DEFAULT_PALETTES, ThemeMode } from '@apps-common/config/theme'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import { queryClient } from '@apps-common/lib'
import { useThemeStore } from '@apps-common/stores/useThemeStore'
import type { PortalThemeModel, ThemePalette, ThemePayload } from '@apps-common/types/portal/theme'
import { hexToHSL, hlsStringToHex } from '@apps-common/utils/helpers'

const APP_PROJECT = process.env.REACT_APP_PROJECT
const hexRegex = /^#([0-9a-f]{3}){1,2}$/i
const coldBootThemeKeysLoaded = new Set<string>()

type DarkModeSubscriber = (result: IsDarkModeResult) => void

const darkModeSubscribers = new Set<DarkModeSubscriber>()
let darkModeSetup: Promise<void> | null = null
let lastDarkModeResult: IsDarkModeResult | undefined
let lastStatusBarSignature: string | undefined

const notifyDarkModeSubscribers = (result: IsDarkModeResult) => {
  lastDarkModeResult = result
  darkModeSubscribers.forEach((subscriber) => subscriber(result))
}

const refreshDarkMode = async () => {
  const result = await DarkMode.isDarkMode()
  notifyDarkModeSubscribers(result)
}

const subscribeToDarkMode = (subscriber: DarkModeSubscriber) => {
  darkModeSubscribers.add(subscriber)

  if (lastDarkModeResult) {
    subscriber(lastDarkModeResult)
  }

  return () => {
    darkModeSubscribers.delete(subscriber)
  }
}

const ensureDarkModeSetup = () => {
  if (darkModeSetup) return darkModeSetup

  darkModeSetup = (async () => {
    await DarkMode.init()
    await DarkMode.addAppearanceListener(notifyDarkModeSubscribers)
    await refreshDarkMode()

    if (!Capacitor.isNativePlatform()) return

    await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        refreshDarkMode().catch(console.error)
      }
    })

    await App.addListener('resume', () => {
      refreshDarkMode().catch(console.error)
    })
  })()

  return darkModeSetup
}

// ----------------------------------
// Helpers
// ----------------------------------

const parsePalettes = (theme?: PortalThemeModel | null): ThemePayload | null => {
  if (!theme) return null
  if ((theme as any).palettes) return (theme as any).palettes as ThemePayload
  if (!(theme as any).palettesJson) return null

  try {
    return JSON.parse((theme as any).palettesJson) as ThemePayload
  } catch {
    return null
  }
}

const resolveMode = (mode: ThemeMode, isSystemDark?: boolean): 'light' | 'dark' => {
  if (mode === ThemeMode.system) return isSystemDark ? 'dark' : 'light'
  return mode === ThemeMode.dark ? 'dark' : 'light'
}

const withPaletteFallbacks = (palette: ThemePalette | null, resolved: 'light' | 'dark'): ThemePalette => {
  const fallback = resolved === 'dark' ? DEFAULT_PALETTES.dark : DEFAULT_PALETTES.light
  const lightFallback = DEFAULT_PALETTES.light
  const p = palette ?? fallback

  return {
    ...fallback,
    ...p,

    logoBg: p.logoBg ?? fallback.logoBg ?? fallback.bg,
    logoPrimary: p.logoPrimary ?? lightFallback.logoPrimary ?? '',
    logoSecondary: p.logoSecondary ?? lightFallback.logoSecondary ?? '',

    myMonitoringBrand: p.myMonitoringBrand ?? fallback.myMonitoringBrand ?? fallback.brand,
    myMonitoringBrand2: p.myMonitoringBrand2 ?? fallback.myMonitoringBrand2 ?? fallback.brand,
    myMonitoringLogoBg: p.myMonitoringLogoBg ?? fallback.myMonitoringLogoBg ?? p.logoBg ?? fallback.logoBg ?? fallback.bg,
    onMyMonitoringBrand: p.onMyMonitoringBrand ?? fallback.onMyMonitoringBrand ?? fallback.onBrand,
    onMyMonitoringBrand2: p.onMyMonitoringBrand2 ?? fallback.onMyMonitoringBrand2 ?? fallback.onBrand
  }
}

const getPaletteForMode = (theme: PortalThemeModel | null | undefined, resolved: 'light' | 'dark'): ThemePalette | null => {
  const palettes = parsePalettes(theme)
  if (!palettes) return null

  return resolved === 'dark' ? { ...palettes.dark } : { ...palettes.light }
}

/**
 * Normalize to an HSL triplet: "H S% L%"
 * - accepts hex "#rrggbb" => converts via hexToHSL
 * - accepts triplet
 * - accepts hsl(...)
 */
const normalizeToHslTriplet = (value?: string | null): string | null => {
  if (!value) return null

  if (hexRegex.test(value)) {
    const triplet = hexToHSL(value)
    return triplet ? triplet.trim() : null
  }

  const raw = value.trim()

  // already triplet-like
  if (/^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/.test(raw)) return raw

  // hsl(225 60% 52%) or hsl(225, 60%, 52%)
  const m = raw.match(/hsl\(\s*([0-9.]+)\s*[, ]\s*([0-9.]+)%\s*[, ]\s*([0-9.]+)%\s*\)/i)
  if (m) {
    const h = Number(m[1])
    const s = Number(m[2])
    const l = Number(m[3])
    if (![h, s, l].some((n) => Number.isNaN(n))) return `${h} ${s}% ${l}%`
  }

  return null
}

/**
 * Skeleton vars based on text triplet with fixed alpha (tenant-agnostic).
 * Stored as "H S% L% / a" so Tailwind can use: hsl(var(--rn-skeleton))
 */
const buildSkeletonFromText = (args: {
  mode: 'light' | 'dark'
  textTriplet: string
}): { skeleton: string; skeletonHi: string; skeletonBorder: string } => {
  const { mode, textTriplet } = args

  // Light stays subtle; Dark slightly stronger but still not "flashy"
  const baseA = mode === 'dark' ? 0.09 : 0.07
  const hiA = mode === 'dark' ? 0.12 : 0.1
  const borderA = mode === 'dark' ? 0.15 : 0.12

  return {
    skeleton: `${textTriplet} / ${baseA}`,
    skeletonHi: `${textTriplet} / ${hiA}`,
    skeletonBorder: `${textTriplet} / ${borderA}`
  }
}

/**
 * iOS Safari toolbar color: most reliable approach is:
 * - remove ALL theme-color metas (including media-based)
 * - insert ONE meta[name="theme-color"]
 * - replace node across frames
 */
const normalizeMetaColor = (value: string): string => {
  const triplet = normalizeToHslTriplet(value)
  if (triplet) return hlsStringToHex(triplet)
  return value
}

const forceIosSafariThemeColor = (color: string) => {
  const head = document.head

  // remove all theme-color tags (including prefers-color-scheme versions)
  const existing = Array.from(document.querySelectorAll('meta[name="theme-color"]'))
  existing.forEach((n) => n.parentNode?.removeChild(n))

  // create a single meta
  const meta = document.createElement('meta')
  meta.setAttribute('name', 'theme-color')
  meta.setAttribute('content', color)
  head.appendChild(meta)

  const replace = () => {
    const m = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
    if (!m) return
    m.setAttribute('content', color)
    const clone = m.cloneNode(true) as HTMLMetaElement
    m.parentNode?.replaceChild(clone, m)
  }

  requestAnimationFrame(replace)
  setTimeout(replace, 50)

  // helps reduce flashes during the switch
  document.documentElement.style.backgroundColor = color
  document.body.style.backgroundColor = color
}

const updateMeta = (name: string, content: string) => {
  const head = document.head
  const meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null

  if (meta) {
    meta.setAttribute('content', content)
    const clone = meta.cloneNode(true) as HTMLMetaElement
    meta.parentNode?.replaceChild(clone, meta)
    return
  }

  const newMeta = document.createElement('meta')
  newMeta.setAttribute('name', name)
  newMeta.setAttribute('content', content)
  head.appendChild(newMeta)
}

const applyNativeStatusBar = async (mode: 'light' | 'dark', backgroundColorValue: string) => {
  if (!Capacitor.isNativePlatform()) return

  const backgroundColor = normalizeMetaColor(backgroundColorValue)
  const style = mode === 'dark' ? Style.Dark : Style.Light
  const signature = `${style}:${backgroundColor}`

  if (signature === lastStatusBarSignature) return
  lastStatusBarSignature = signature

  await StatusBar.setStyle({ style })
  await StatusBar.setBackgroundColor({ color: backgroundColor })
}

// ----------------------------------
// Hook
// ----------------------------------

export const useTheme = () => {
  const [darkModeResult, setDarkModeResult] = useState<IsDarkModeResult>()
  const user = usePortalFirebaseUser()

  const { theme, setTheme, themeMode, setThemeMode, modifiedTheme, contextTheme, setContextTheme, companyId, setCompanyId } = useThemeStore()

  const { data: portalTheme, refetch: portalThemeRefetch, isFetchedAfterMount: portalThemeFetchedAfterMount } = useGetPortalTheme({
    config: {
      enabled: APP_PROJECT === 'radionet-portal' && !!user && !!companyId,
      staleTime: 0,
      refetchOnMount: 'always'
    },
    companyId
  })

  const { data: mmTheme, refetch: mmThemeRefetch, isFetchedAfterMount: mmThemeFetchedAfterMount } = useGetMyMonitoringTheme({
    config: {
      enabled: APP_PROJECT === 'my-monitoring' && !!user,
      staleTime: 0,
      refetchOnMount: 'always'
    },
    companyId: ''
  })

  const { data: mmTestTheme, refetch: mmTestThemeRefetch, isFetchedAfterMount: mmTestThemeFetchedAfterMount } = useGetMyMonitoringTheme({
    config: {
      enabled: APP_PROJECT === 'my-monitoring' && !!user && !!companyId,
      staleTime: 0,
      refetchOnMount: 'always'
    },
    companyId
  })

  const resetCompany = () => {
    setCompanyId(undefined)
    queryClient.resetQueries({ queryKey: QUERY_GET_MY_MONITORING_THEME() })
    queryClient.resetQueries({ queryKey: QUERY_GET_PORTAL_THEME() })
  }

  const resetContextTheme = () => {
    setContextTheme(null)
    if (companyId) {
      queryClient.setQueryData(QUERY_GET_MY_MONITORING_THEME(companyId), undefined)
    }
  }

  const data = useMemo(() => (mmTheme || portalTheme) as PortalThemeModel | undefined, [mmTheme, portalTheme])
  // const themeFetchedAfterMount = APP_PROJECT === 'my-monitoring' ? mmThemeFetchedAfterMount : portalThemeFetchedAfterMount

  useEffect(() => {
    if (!companyId && user?.companyId) setCompanyId(user.companyId)
  }, [companyId, setCompanyId, user?.companyId])

  useEffect(() => {
    if (!user || !data) return
    // if (coldBootThemeKeysLoaded.has(coldBootThemeKey)) return

    setTheme(data)
    // coldBootThemeKeysLoaded.add(coldBootThemeKey)
  }, [user, data, setTheme])

  // ----------------------------------
  // Dark mode listener
  // ----------------------------------

  useEffect(() => {
    const unsubscribe = subscribeToDarkMode((result) => {
      setDarkModeResult((current) => (current?.dark === result.dark ? current : result))
    })

    ensureDarkModeSetup().catch(console.error)

    return unsubscribe
  }, [])

  const resolvedMode = resolveMode(themeMode, darkModeResult?.dark)

  // ----------------------------------
  // Build active palette
  // ----------------------------------

  const currentPalette = useMemo(() => {
    const base = (modifiedTheme ?? theme) as PortalThemeModel | undefined
    return getPaletteForMode(base, resolvedMode)
  }, [theme, modifiedTheme, resolvedMode])

  const resolvedAppPalette = useMemo(() => withPaletteFallbacks(currentPalette, resolvedMode), [currentPalette, resolvedMode])

  const activeContextTheme = useMemo(() => mmTestTheme ?? contextTheme, [mmTestTheme, contextTheme])

  const contextPalette = useMemo(() => {
    return activeContextTheme ? getPaletteForMode(activeContextTheme, resolvedMode) ?? currentPalette : null
  }, [
    activeContextTheme,
    currentPalette,
    resolvedMode
  ])

  const resolvedContextPalette = useMemo(
    () => (activeContextTheme ? withPaletteFallbacks(contextPalette, resolvedMode) : null),
    [activeContextTheme, contextPalette, resolvedMode]
  )
  const resolvedPalette = resolvedAppPalette

  // ----------------------------------
  // Apply to CSS + iOS Safari meta
  // ----------------------------------

  const setCss = (name: string, value?: string | null) => {
    if (!value) return

    if (hexRegex.test(value)) {
      const hsl = hexToHSL(value)
      if (hsl) document.documentElement.style.setProperty(name, hsl)
      return
    }

    document.documentElement.style.setProperty(name, value)
  }

  const applyPaletteVariables = (prefix: string, p: ThemePalette) => {
    // Brand
    setCss(`${prefix}-brand`, p.brand)
    setCss(`${prefix}-on-brand`, p.onBrand)

    // Surfaces
    setCss(`${prefix}-bg`, p.bg)
    setCss(`${prefix}-surface`, p.surface)
    setCss(`${prefix}-surface-2`, p.surface2)
    setCss(`${prefix}-border`, p.border)

    // Text
    setCss(`${prefix}-text`, p.text)
    setCss(`${prefix}-text-muted`, p.textMuted)

    // Status
    setCss(`${prefix}-success`, p.success)
    setCss(`${prefix}-on-success`, p.onSuccess)
    setCss(`${prefix}-warning`, p.warning)
    setCss(`${prefix}-on-warning`, p.onWarning)
    setCss(`${prefix}-error`, p.error)
    setCss(`${prefix}-on-error`, p.onError)
    setCss(`${prefix}-info`, p.info)
    setCss(`${prefix}-on-info`, p.onInfo)

    // UI
    setCss(`${prefix}-focus`, p.focus)
    setCss(`${prefix}-disabled-bg`, p.disabledBg)
    setCss(`${prefix}-disabled-text`, p.disabledText)

    // Logos
    document.documentElement.style.setProperty(`${prefix}-logo-bg`, p.logoBg || '')
    document.documentElement.style.setProperty(`${prefix}-logo-primary`, `url("${p.logoPrimary || ''}")`)
    document.documentElement.style.setProperty(`${prefix}-logo-secondary`, `url("${p.logoSecondary || ''}")`)

    // MyMonitoring
    setCss(`${prefix}-mm-brand`, p.myMonitoringBrand)
    setCss(`${prefix}-mm-brand-2`, p.myMonitoringBrand2)
    setCss(`${prefix}-mm-logo-bg`, p.myMonitoringLogoBg)
    setCss(`${prefix}-on-mm-brand`, p.onMyMonitoringBrand)
    setCss(`${prefix}-on-mm-brand-2`, p.onMyMonitoringBrand2)
  }

  const clearPaletteVariables = (prefix: string) => {
    const keys = [
      'brand',
      'on-brand',
      'bg',
      'surface',
      'surface-2',
      'border',
      'text',
      'text-muted',
      'success',
      'on-success',
      'warning',
      'on-warning',
      'error',
      'on-error',
      'info',
      'on-info',
      'focus',
      'disabled-bg',
      'disabled-text',
      'logo-bg',
      'logo-primary',
      'logo-secondary',
      'mm-brand',
      'mm-brand-2',
      'mm-logo-bg',
      'on-mm-brand',
      'on-mm-brand-2'
    ]

    keys.forEach((key) => document.documentElement.style.removeProperty(`${prefix}-${key}`))
    document.documentElement.style.removeProperty(`${prefix}-mm-logo-bg-alpha`)
  }

  const applySkeletonVariables = (prefix: string, p: ThemePalette) => {
    const textTriplet =
      normalizeToHslTriplet(p.textMuted) ?? normalizeToHslTriplet(p.text) ?? (resolvedMode === 'dark' ? '0 0% 100%' : '0 0% 0%')

    const sk = buildSkeletonFromText({ mode: resolvedMode, textTriplet })
    document.documentElement.style.setProperty(`${prefix}-skeleton`, sk.skeleton)
    document.documentElement.style.setProperty(`${prefix}-skeleton-hi`, sk.skeletonHi)
    document.documentElement.style.setProperty(`${prefix}-skeleton-border`, sk.skeletonBorder)
  }

  const clearSkeletonVariables = (prefix: string) => {
    document.documentElement.style.removeProperty(`${prefix}-skeleton`)
    document.documentElement.style.removeProperty(`${prefix}-skeleton-hi`)
    document.documentElement.style.removeProperty(`${prefix}-skeleton-border`)
  }

  const applyPalette = (
    appPalette: ThemePalette,
    contextPaletteValue: ThemePalette | null,
    contextPaletteSource: ThemePalette | null
  ) => {
    document.documentElement.classList.toggle('dark', resolvedMode === 'dark')

    // iOS Safari: force toolbar color to follow the main app theme
    forceIosSafariThemeColor(appPalette.bg)
    applyNativeStatusBar(resolvedMode, appPalette.bg).catch(console.error)
    document.documentElement.style.setProperty('background', 'hsl(var(--rn-bg)) !important')
    document.body.style.setProperty('background', 'hsl(var(--rn-bg)) !important')

    document.documentElement.style.setProperty('background-color', 'hsl(var(--rn-bg)) !important')
    document.body.style.setProperty('background-color', 'hsl(var(--rn-bg)) !important')

    document.documentElement.style.setProperty('--ion-background-color', 'hsl(var(--rn-bg)) !important')
    document.body.style.setProperty('--ion-background-color', 'hsl(var(--rn-bg)) !important')

    // Helpful for iOS PWA / sometimes Safari too
    updateMeta('apple-mobile-web-app-status-bar-style', resolvedMode === 'dark' ? 'black' : 'default')
    // Optional (if you care about Windows tiles / etc)
    updateMeta('msapplication-TileColor', appPalette.bg)

    applyPaletteVariables('--rn', appPalette)
    applySkeletonVariables('--rn', appPalette)

    if (contextPaletteValue) {
      applyPaletteVariables('--rn-ctx', contextPaletteValue)

      if (contextPaletteSource?.myMonitoringLogoBg === null) {
        document.documentElement.style.setProperty('--rn-ctx-mm-logo-bg', '0 0% 0%')
        document.documentElement.style.setProperty('--rn-ctx-mm-logo-bg-alpha', '0')
      } else {
        document.documentElement.style.removeProperty('--rn-ctx-mm-logo-bg-alpha')
      }

      applySkeletonVariables('--rn-ctx', contextPaletteValue)
    } else {
      clearPaletteVariables('--rn-ctx')
      clearSkeletonVariables('--rn-ctx')
    }
  }

  useEffect(() => {
    if (darkModeResult) applyPalette(resolvedAppPalette, resolvedContextPalette, contextPalette)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAppPalette, resolvedContextPalette, contextPalette, darkModeResult, resolvedMode])

  const getLogoUrl = (type: 'primary' | 'secondary') => (type === 'primary' ? resolvedAppPalette.logoPrimary : resolvedAppPalette.logoSecondary)

  const isUsingDarkMode = useMemo(() => {
    if (themeMode === 'system') {
      return darkModeResult?.dark || false
    }

    return themeMode === 'dark'
  }, [themeMode, darkModeResult?.dark])

  return {
    currentTheme: currentPalette,
    resolvedTheme: resolvedPalette,
    currentAppTheme: currentPalette,
    resolvedAppTheme: resolvedAppPalette,
    currentContextTheme: activeContextTheme ? contextPalette ?? currentPalette : null,
    resolvedContextTheme: activeContextTheme ? resolvedContextPalette ?? resolvedAppPalette : null,
    hasContextTheme: !!activeContextTheme,
    theme,
    contextTheme: activeContextTheme,

    // allow ThemeSelector to call through hook if you want
    themeMode,
    setThemeMode,
    globalThemeMode: resolvedMode,

    isDarkMode: darkModeResult?.dark,
    isUsingDarkMode,
    refetch: APP_PROJECT === 'my-monitoring' ? mmThemeRefetch : portalThemeRefetch,
    companyId,
    changeCompanyId: setCompanyId,
    resetCompany,
    resetContextTheme,
    changeContextTheme: setContextTheme,
    getLogoUrl,
  }
}
